import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import UserAvatar from './UserAvatar';
import getUserRoleFlags from "../hooks/useGetUserRoleFlags";
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { useThemeMode } from './ThemeContext';
import { AppHeader } from './AppHeader';
import LeaveEventCreatorDialog from './LeaveEventCreatorDialog';
import React from 'react';

const navButtonStyle = {
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  pl: 1.5,
  pr: 1.5,
};

const CREATOR_PATH = "/event-configurator";

const NavigationBar = () => {
  const { isAdmin, isCreator } = getUserRoleFlags();
  const { mode, toggleMode } = useThemeMode();
  const { hasUnsavedChanges, setHasUnsavedChanges, discardDraft } = useUnsavedChanges();
  const navigate = useNavigate();
  const location = useLocation();
  const [leaveDialogOpen, setLeaveDialogOpen] = React.useState(false);
  const [pendingRoute, setPendingRoute] = React.useState<string | null>(null);
  const [leaveBusy, setLeaveBusy] = React.useState(false);
  const pendingRouteRef = React.useRef<string | null>(null);

  const onCreatorRoute = location.pathname.startsWith(CREATOR_PATH);

  const openLeaveDialog = React.useCallback((targetPath: string) => {
    pendingRouteRef.current = targetPath;
    setPendingRoute(targetPath);
    setLeaveDialogOpen(true);
  }, []);

  const confirmNavigationIfNeeded = (to: string): boolean => {
    if (!hasUnsavedChanges || !onCreatorRoute) return true;
    openLeaveDialog(to);
    return false;
  };

  const continuePendingNavigation = React.useCallback(() => {
    const target = pendingRouteRef.current;
    pendingRouteRef.current = null;
    setPendingRoute(null);
    if (!target) return;
    if (target === CREATOR_PATH) {
      navigate(target, { state: { resetCreatorAt: Date.now() } });
    } else {
      navigate(target);
    }
  }, [navigate]);

  const handleNavigate = (to: string) => {
    if (location.pathname === to && to !== CREATOR_PATH) return;
    if (!confirmNavigationIfNeeded(to)) return;
    if (to === CREATOR_PATH) {
      navigate(to, { state: { resetCreatorAt: Date.now() } });
      return;
    }
    navigate(to);
  };

  const closeLeaveDialog = React.useCallback(() => {
    if (leaveBusy) return;
    setLeaveDialogOpen(false);
    pendingRouteRef.current = null;
    setPendingRoute(null);
  }, [leaveBusy]);

  const handleLeaveKeepDraft = React.useCallback(() => {
    setHasUnsavedChanges(false);
    setLeaveDialogOpen(false);
    continuePendingNavigation();
  }, [continuePendingNavigation, setHasUnsavedChanges]);

  const handleDiscardAndLeave = React.useCallback(async () => {
    try {
      setLeaveBusy(true);
      await discardDraft();
      setHasUnsavedChanges(false);
      setLeaveDialogOpen(false);
      continuePendingNavigation();
    } finally {
      setLeaveBusy(false);
    }
  }, [continuePendingNavigation, discardDraft, setHasUnsavedChanges]);

  // Browser back/forward only — restore creator via React Router (never history.go(1)).
  React.useEffect(() => {
    if (!hasUnsavedChanges) return;

    const onPopState = () => {
      const path = window.location.pathname;
      if (path.startsWith(CREATOR_PATH)) return;
      const target = path + window.location.search;
      openLeaveDialog(target);
      navigate(CREATOR_PATH, { replace: true });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [hasUnsavedChanges, navigate, openLeaveDialog]);

  return (
    <>
      <AppHeader
        // Same asset as VehicleDebugger and EngineeringTools; see AppHeader.
        logoSrc="/neura-logo.svg"
        logoAlt="NEURA Mobile Robots"
        productName="Event Manager"
        // Stays English while the rest of this tool's UI is English. When EventManager gets
        // i18n these three move into its locale files, like EngineeringTools already did.
        tagline="Manage AGV event definitions"
        mode={mode}
        onToggleMode={toggleMode}
        themeLabel={mode === 'light' ? 'Dark mode' : 'Light mode'}
        onBrandClick={() => handleNavigate("/")}
        // This header carries the navigation, which used to stay visible because an inner box
        // scrolled instead of the document. Sticky keeps that behaviour.
        sticky
        actions={
          <>
            <Box>
              <Button sx={navButtonStyle} color="inherit" onClick={() => handleNavigate("/")}>Home</Button>
              {(isCreator || isAdmin) && (
                <Button sx={navButtonStyle} color="inherit" onClick={() => handleNavigate(CREATOR_PATH)}>Creator</Button>
              )}
              {isAdmin && (
                <>
                  <Button sx={navButtonStyle} color="inherit" onClick={() => handleNavigate("/admin")}>Administration</Button>
                </>
              )}
              <Button sx={navButtonStyle} color="inherit" onClick={() => handleNavigate("/about")}>About</Button>
            </Box>
            <UserAvatar />
          </>
        }
      />
      <LeaveEventCreatorDialog
        open={leaveDialogOpen}
        onClose={closeLeaveDialog}
        onStay={closeLeaveDialog}
        onLeaveKeepDraft={handleLeaveKeepDraft}
        onDiscardAndLeave={() => void handleDiscardAndLeave()}
        leaveBusy={leaveBusy}
      />

    </>
  );
};

export default NavigationBar;
